import { useState } from "react";
import Icon from "./Icon.jsx";
import TrackCard from "./TrackCard.jsx";
import { TRACKS } from "../data/curriculum.js";
import { generateCourse } from "../services/claude.js";

export default function Landing({ onStartTrack, onCustomTopic }) {
  const [query, setQuery] = useState("");
  const [generating, setGenerating] = useState(false);
  const [suggestion, setSuggestion] = useState(null);

  const filtered = query.trim()
    ? TRACKS.filter(
        (t) =>
          t.title.toLowerCase().includes(query.toLowerCase()) ||
          t.subtitle.toLowerCase().includes(query.toLowerCase())
      )
    : TRACKS;

  const generateCustom = async () => {
    if (!query.trim()) return;
    setGenerating(true);
    setSuggestion(null);
    try {
      const out = await generateCourse(query);
      const match = out.match(/\{[\s\S]*\}/);
      if (match) setSuggestion(JSON.parse(match[0]));
      else setSuggestion({ error: true });
    } catch {
      setSuggestion({ error: true });
    }
    setGenerating(false);
  };

  return (
    <div className="page fade-up">
      <section style={{ textAlign: "center", padding: "56px 0 40px", position: "relative" }}>
        <div className="eyebrow" style={{ marginBottom: 18 }}>A gamified school for AI</div>
        <h1
          className="serif"
          style={{
            fontSize: "clamp(56px, 8vw, 104px)",
            lineHeight: 0.98,
            margin: "0 0 20px",
            letterSpacing: "-0.02em",
          }}
        >
          Learn Claude,
          <br />
          <span style={{ fontStyle: "italic", color: "var(--terracotta)" }}>one craft at a time.</span>
        </h1>
        <p style={{ fontSize: 18, color: "var(--ink-3)", maxWidth: 620, margin: "0 auto 40px", lineHeight: 1.55 }}>
          Bite-sized lessons, practice that actually sticks, and a certificate you'll be slightly proud of.
          Pick a track, or type a topic and we'll build the course around you.
        </p>

        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div
            className="card-raised"
            style={{
              display: "flex",
              alignItems: "center",
              padding: 8,
              gap: 8,
              borderRadius: 999,
              background: "#fff",
            }}
          >
            <span style={{ paddingLeft: 14, color: "var(--ink-3)" }}>
              <Icon name="search" size={18} />
            </span>
            <label htmlFor="topic-input" className="sr-only" style={{ position: "absolute", left: -9999 }}>
              Topic to learn
            </label>
            <input
              id="topic-input"
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSuggestion(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") generateCustom();
              }}
              placeholder="What do you want to learn? e.g. prompt engineering, Claude Code…"
              style={{
                flex: 1,
                border: "none",
                outline: "none",
                background: "transparent",
                fontSize: 16,
                fontFamily: "inherit",
                padding: "10px 4px",
              }}
            />
            <button
              className="btn btn-primary"
              onClick={generateCustom}
              disabled={generating || !query.trim()}
              style={{ borderRadius: 999 }}
            >
              {generating ? "Designing…" : <>Design my course <Icon name="arrow-right" size={14} /></>}
            </button>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 14, flexWrap: "wrap" }}>
            {["prompt engineering", "claude code basics", "writing with AI", "design a weather app"].map((s) => (
              <button key={s} className="chip" style={{ cursor: "pointer" }} onClick={() => setQuery(s)}>
                {s}
              </button>
            ))}
          </div>
        </div>

        {suggestion && !suggestion.error && (
          <div
            className="card-raised fade-up"
            style={{ maxWidth: 720, margin: "28px auto 0", padding: 28, textAlign: "left", position: "relative" }}
          >
            <div style={{ position: "absolute", top: 18, right: 18 }}>
              <span
                className="chip"
                style={{ background: "var(--terracotta-wash)", borderColor: "var(--terracotta-2)", color: "var(--terracotta-3)" }}
              >
                <Icon name="sparkle" size={12} /> Designed for you
              </span>
            </div>
            <div className="eyebrow" style={{ marginBottom: 8 }}>
              {suggestion.level} · {suggestion.minutes} min · {suggestion.lessons} lessons
            </div>
            <h2 className="serif" style={{ fontSize: 40, margin: "0 0 4px", letterSpacing: "-0.015em" }}>
              {suggestion.title}
            </h2>
            <p style={{ color: "var(--ink-3)", marginTop: 0, marginBottom: 24 }}>{suggestion.subtitle}</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 14 }}>
              {suggestion.units?.map((u, i) => (
                <div
                  key={i}
                  style={{
                    border: "1px solid var(--line)",
                    borderRadius: 12,
                    padding: 14,
                    background: "var(--cream-2)",
                  }}
                >
                  <div className="eyebrow" style={{ marginBottom: 8 }}>Unit {i + 1}</div>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>{u.title}</div>
                  {u.bullets?.map((b, j) => (
                    <div key={j} style={{ fontSize: 13, color: "var(--ink-3)", marginBottom: 4 }}>
                      · {b}
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 22, display: "flex", gap: 10 }}>
              <button className="btn btn-terracotta" onClick={() => onCustomTopic(suggestion, query)}>
                Start this course <Icon name="arrow-right" size={14} color="#fff" />
              </button>
              <button className="btn btn-ghost" onClick={generateCustom}>Regenerate</button>
            </div>
          </div>
        )}
        {suggestion?.error && (
          <div style={{ maxWidth: 640, margin: "20px auto 0", color: "var(--ink-3)", fontSize: 14 }}>
            Couldn't draft a course just now. Try one of the tracks below ↓
          </div>
        )}
      </section>

      <section style={{ marginTop: 60 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 20 }}>
          <h2 className="serif" style={{ fontSize: 34, margin: 0, letterSpacing: "-0.01em" }}>
            Popular tracks
          </h2>
          <span className="eyebrow">
            {filtered.length} of {TRACKS.length}
          </span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 16 }}>
          {filtered.map((t) => (
            <TrackCard key={t.id} track={t} onClick={() => onStartTrack(t)} />
          ))}
        </div>
      </section>

      <section
        style={{
          marginTop: 64,
          padding: "36px 40px",
          borderRadius: 22,
          background: "var(--ink)",
          color: "var(--cream)",
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 32,
        }}
      >
        {[
          { n: "42,180", l: "learners this week" },
          { n: "1.2M", l: "gems earned today" },
          { n: "96%", l: "finish their first unit" },
          { n: "4.9★", l: "average lesson rating" },
        ].map((s) => (
          <div key={s.l}>
            <div className="serif" style={{ fontSize: 40, letterSpacing: "-0.02em" }}>
              {s.n}
            </div>
            <div style={{ color: "var(--cream-3)", fontSize: 13 }}>{s.l}</div>
          </div>
        ))}
      </section>

      <section style={{ marginTop: 64 }}>
        <h2 className="serif" style={{ fontSize: 34, margin: "0 0 24px", letterSpacing: "-0.01em" }}>
          How it works
        </h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14 }}>
          {[
            { n: "01", t: "Pick or invent a track", d: "Choose a curated path, or type your own topic — Claude designs the syllabus." },
            { n: "02", t: "Learn in small bites", d: "4-minute lessons. Real examples. No filler." },
            { n: "03", t: "Practice with checkpoints", d: "Multiple choice, fill-in, code, and open-ended answers graded by Claude." },
            { n: "04", t: "Earn gems, XP & a certificate", d: "Finish your track, get a scored certificate. Share it if you want." },
          ].map((s) => (
            <div key={s.n} className="card" style={{ padding: 22 }}>
              <div className="mono" style={{ fontSize: 12, color: "var(--terracotta)", marginBottom: 10 }}>
                {s.n}
              </div>
              <div className="serif" style={{ fontSize: 22, marginBottom: 8 }}>{s.t}</div>
              <div style={{ fontSize: 14, color: "var(--ink-3)", lineHeight: 1.55 }}>{s.d}</div>
            </div>
          ))}
        </div>
      </section>

      <div style={{ textAlign: "center", marginTop: 60, color: "var(--ink-4)", fontSize: 13 }}>
        <span className="mono">v1.0</span> · made with care, cream, and a little terracotta
      </div>
    </div>
  );
}
